import {
  useGetLessonWhiteboardErpQuery,
  useLessonHeaderForWhiteboardQuery,
  GetLessonWhiteboardErpQuery,
  LessonHeaderForWhiteboardQuery,
} from 'core/graphql';

export type ILessonWhiteboard = NonNullable<
  GetLessonWhiteboardErpQuery['lessonWhiteboardForErp']
>;
export type IWhiteboardLessonHeader = NonNullable<
  LessonHeaderForWhiteboardQuery['zoomLesson']
>;

interface ILessonWhiteboardHook {
  whiteboard: ILessonWhiteboard | null;
  lesson?: IWhiteboardLessonHeader;
  loading: boolean;
  /**
   * Board query alone. The light header query resolves first, so a page that
   * keys the empty state off `loading` would flash «Доска не создана» over a
   * board that is still downloading.
   */
  boardLoading: boolean;
  error?: string;
}

/**
 * Spec 0010 — data seam for the `/whiteboard/lesson/:id` read-only page.
 *
 * Runs two ERP-scoped queries keyed by the GroupLesson id:
 *   - `lessonWhiteboardForErp` — the board itself (null → «Доска не создана»),
 *   - a light `zoomLesson` header selection — lesson name/date + owning
 *     group for the back-link (same ADMIN/ACCOUNT_MANAGER/METHODIST gate).
 *
 * `whiteboard.files` (rev.3) rides along on the board result: presigned S3
 * links for the board's images, which `ExcalidrawViewer` fetches and injects
 * via `addFiles`. They are short-TTL — hence `cache-and-network`, so a board
 * reopened from cache re-fetches rather than serving dead urls.
 */
export const useLessonWhiteboard = (
  lessonId?: string,
): ILessonWhiteboardHook => {
  const {
    data: boardData,
    loading: boardLoading,
    error: boardError,
  } = useGetLessonWhiteboardErpQuery({
    variables: { lessonId: lessonId || '' },
    skip: !lessonId,
    fetchPolicy: 'cache-and-network',
  });

  const {
    data: lessonData,
    loading: lessonLoading,
    error: lessonError,
  } = useLessonHeaderForWhiteboardQuery({
    variables: { groupLessonId: lessonId || '' },
    skip: !lessonId,
  });

  return {
    whiteboard: boardData?.lessonWhiteboardForErp ?? null,
    lesson: lessonData?.zoomLesson ?? undefined,
    loading: boardLoading || lessonLoading,
    // cache-and-network refetches keep `boardLoading` true with data already
    // in hand — only treat it as "still loading" while there is nothing to show.
    boardLoading: boardLoading && !boardData,
    error: boardError?.message || lessonError?.message,
  };
};
